import { useEffect, useState } from 'react'
import { api, type ResolutionCaseFull, type ResolutionOutcome } from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { icons } from '../components/icons.ts'
import { ResolutionCaseRun } from '../components/ResolutionCaseRun.tsx'
import { ResolutionRuns } from '../components/ResolutionRuns.tsx'
import { ResolutionCaseEditor } from '../components/ResolutionCaseEditor.tsx'
import { CaseStatus } from '../components/CaseStatus.tsx'

/**
 * One resolution case: what should happen, and what did.
 *
 * The screen exists because a number cannot be acted on. CI can tell you the
 * set regressed; only this can tell you that retrieval never surfaced the
 * pharmacist's product, that the model scored a cryptic ERP name at 0.2, and
 * that a rival with a tidy name cleared the margin — which is three different
 * fixes wearing one failed assertion.
 *
 * Running costs a model call per ingredient, so it is a button and never an
 * effect.
 */

const OUTCOME: Readonly<Record<ResolutionOutcome, string>> = {
  consistent: 'Concorda',
  suggest_correction: 'Sugere correção',
  unresolved: 'Não conclui',
}

export function ResolutionCase({ caseId, onBack }: { caseId: string; onBack: () => void }) {
  const [kase, setCase] = useState<ResolutionCaseFull | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [running, setRunning] = useState(false)
  const [history, setHistory] = useState(false)
  const [editing, setEditing] = useState(false)
  const [promoting, setPromoting] = useState(false)
  const [problems, setProblems] = useState<string[]>([])

  useEffect(() => {
    setCase(undefined)
    void api
      .resolutionCase(caseId)
      .then((r) => {
        setCase(r.case)
        setProblems(r.problems ?? [])
        // An empty case has nothing to read, so reading it is not the useful
        // act -- writing it is. Opening straight into the editor saves a click
        // that has only one sensible answer.
        setEditing(r.case.ingredients.length === 0 && r.case.catalogue.length === 0)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }, [caseId])

  /** Promotes or demotes, which is a save of the same case with a new status. */
  async function setStatus(next: 'draft' | 'corrected') {
    if (kase === undefined) return
    setPromoting(true)
    setError(undefined)
    const updated = {
      ...kase,
      status: next,
      correctedAt: next === 'corrected' ? new Date().toISOString().slice(0, 10) : null,
    }
    try {
      await api.saveResolutionCase(caseId, updated)
      setCase(updated)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao mudar a situação')
    } finally {
      setPromoting(false)
    }
  }

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Resolução
        </Button>
        <h1 className="text-tH4 text-text-strong">{caseId}</h1>
        <span className="flex-1" />
        {!editing && (
          <Button variant="secondary" tone="neutral" size="small" onClick={() => setEditing(true)}>
            Editar
          </Button>
        )}
        <Button
          variant="secondary"
          tone="neutral"
          size="small"
          title="Execuções anteriores deste caso, da mais recente para a mais antiga"
          onClick={() => setHistory(true)}
        >
          Execuções
        </Button>
        {/*
          The same word, icon and modal as the reading set's: one act on two
          answer keys, and two names for it made them look like two features.
        */}
        <Button
          variant="secondary"
          tone="brand"
          size="small"
          leadingIcon={icons.goldenSet}
          disabled={kase === undefined}
          title="Rodar este caso contra o resolvedor e comparar com o esperado"
          onClick={() => setRunning(true)}
        >
          Rodar
        </Button>
      </div>

      {history && <ResolutionRuns caseId={caseId} onClose={() => setHistory(false)} />}

      {running && kase !== undefined && (
        <ResolutionCaseRun kase={kase} onClose={() => setRunning(false)} />
      )}

      {kase !== undefined && !editing && (
        <CaseStatus
          status={kase.status}
          busy={promoting}
          onChange={(next) => void setStatus(next)}
          {...(problems.length > 0 ? { undecided: `Falta completar: ${problems.join('; ')}` } : {})}
        />
      )}

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}
      {kase?.notes !== undefined && kase.notes !== '' && (
        <p className="text-tP text-text-weak max-w-prose">{kase.notes}</p>
      )}

      {kase !== undefined && editing && (
        <ResolutionCaseEditor
          initial={kase}
          onCancel={() => setEditing(false)}
          onSaved={(saved) => {
            setCase(saved)
            setEditing(false)
          }}
        />
      )}

      {kase !== undefined && !editing && (
        <>
          <section className="flex flex-col gap-2">
            <h2 className="text-tPSemi text-text-strong">Ingredientes</h2>
            <div className="rounded-base border-stroke-weak bg-background-raised w-full overflow-x-auto border">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Prescrito', 'Selecionado', 'Esperado', 'Por quê'].map((h) => (
                      <th
                        key={h}
                        scope="col"
                        className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-3 text-left"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kase.ingredients.map((ingredient, index) => {
                    const selected = kase.catalogue.find((p) => p.erpCode === ingredient.selected)
                    return (
                      <tr key={`${ingredient.prescribed}-${index}`}>
                        <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3">
                          {ingredient.prescribed}
                          {ingredient.dose !== null && (
                            <span className="text-tTiny text-text-weak ml-2">
                              {ingredient.dose}
                            </span>
                          )}
                        </td>
                        <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3">
                          <span className="font-mono">{ingredient.selected}</span>
                          {selected !== undefined && (
                            <span className="text-tTiny text-text-weak ml-2">{selected.name}</span>
                          )}
                        </td>
                        <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3">
                          {OUTCOME[ingredient.expect]}
                          {ingredient.expectRival !== null && (
                            <span className="text-tTiny text-text-weak ml-1 font-mono">
                              → {ingredient.expectRival}
                            </span>
                          )}
                        </td>
                        <td className="border-stroke-weak text-tTiny text-text-weak max-w-prose border-b px-3 py-3">
                          {ingredient.why}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h2 className="text-tPSemi text-text-strong">Catálogo fixado</h2>
            <p className="text-tSmall text-text-weak max-w-prose">
              O caso é julgado só contra estes produtos. Um catálogo implícito faria a resposta
              certa mudar sozinha na próxima sincronização do ERP.
            </p>
            <div className="rounded-base border-stroke-weak bg-background-raised w-full overflow-x-auto border">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Código', 'Nome no ERP', 'Sinônimos'].map((h) => (
                      <th
                        key={h}
                        scope="col"
                        className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-3 text-left"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kase.catalogue.map((product) => (
                    <tr key={product.erpCode}>
                      <td className="border-stroke-weak text-tP text-text-strong border-b px-3 py-3 font-mono">
                        {product.erpCode}
                      </td>
                      <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3">
                        {product.name}
                      </td>
                      <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3">
                        {product.synonyms.length === 0 ? '—' : product.synonyms.join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
